import { lazy, Suspense } from 'react';
import { Box, Button, CircularProgress } from '@mui/material';
import { Navigate, Route, Routes } from 'react-router-dom';
import { useAuth } from './auth';
import { Layout } from './components/Layout';
import { RoleGate } from './components/RoleGate';
import { Unauthorized } from './pages/Unauthorized';

// Lazy-loaded pages — each becomes its own chunk, loaded on first navigation.
const Dashboard = lazy(() => import('./pages/Dashboard').then(m => ({ default: m.Dashboard })));
const About = lazy(() => import('./pages/About').then(m => ({ default: m.About })));
const Agenda = lazy(() => import('./pages/Agenda').then(m => ({ default: m.Agenda })));
const Execution = lazy(() => import('./pages/Execution').then(m => ({ default: m.Execution })));
const History = lazy(() => import('./pages/History').then(m => ({ default: m.History })));
const Decisions = lazy(() => import('./pages/Decisions').then(m => ({ default: m.Decisions })));
const Tasks = lazy(() => import('./pages/Tasks').then(m => ({ default: m.Tasks })));
const Workshops = lazy(() => import('./pages/Workshops').then(m => ({ default: m.Workshops })));
const UsersAdmin = lazy(() => import('./pages/UsersAdmin').then(m => ({ default: m.UsersAdmin })));
const Settings = lazy(() => import('./pages/Settings').then(m => ({ default: m.Settings })));
const Audit = lazy(() => import('./pages/Audit').then(m => ({ default: m.Audit })));

function PageLoader() {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', pt: 10 }}>
      <CircularProgress size={36} />
    </Box>
  );
}

export default function App() {
  const { state, refresh } = useAuth();

  if (state.status === 'loading') {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress size={56} />
      </Box>
    );
  }

  if (state.status === 'unauthorized') {
    return <Unauthorized message={state.message} />;
  }

  if (state.status === 'error') {
    return (
      <Box sx={{ p: 6, maxWidth: 640, mx: 'auto', textAlign: 'center' }}>
        <h2 style={{ color: '#0B2545' }}>We couldn't load Workshop Planner</h2>
        <p style={{ color: '#334155' }}>
          This is usually temporary — the service may have been waking up or updating.
          Trying again normally fixes it.
        </p>
        <Button variant="contained" onClick={() => refresh()} sx={{ mt: 1, mb: 3 }}>
          Try again
        </Button>
        <p style={{ color: '#64748B', fontSize: 13 }}>
          If it keeps happening, contact mohanj@softura.com and include the detail below.
        </p>
        <pre style={{
          background: '#F1F5F9', color: '#64748B', padding: 12, borderRadius: 8,
          whiteSpace: 'pre-wrap', fontSize: 12, textAlign: 'left',
        }}>{state.message}</pre>
      </Box>
    );
  }

  if (state.status !== 'authenticated') {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress size={56} />
      </Box>
    );
  }

  return (
    <Layout>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/about" element={<About />} />
          <Route path="/workshops" element={<RoleGate allow={['SuperAdmin', 'Admin']}><Workshops /></RoleGate>} />
          <Route path="/agenda" element={<Agenda />} />
          <Route path="/execution" element={<Execution />} />
          <Route path="/history" element={<History />} />
          <Route path="/decisions" element={<Decisions />} />
          <Route path="/tasks" element={<Tasks />} />
          <Route
            path="/users"
            element={
              <RoleGate allow={['SuperAdmin', 'Admin']}>
                <UsersAdmin />
              </RoleGate>
            }
          />
          <Route
            path="/settings"
            element={
              <RoleGate allow={['SuperAdmin']}>
                <Settings />
              </RoleGate>
            }
          />
          <Route
            path="/audit"
            element={
              <RoleGate allow={['SuperAdmin', 'Admin']}>
                <Audit />
              </RoleGate>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </Layout>
  );
}
